import React, { useState, useEffect, useRef, useMemo } from "react";
import * as XLSX from "xlsx";
import { Search, Download, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

const SupportStyleTable = ({
  columns = [],
  rows = [],
  tableId = "table",
  fileName: customFileName = null,
  emptyComponent = null,
  onRowClick = null,
  itemsPerPage = 50,
  maxPageNumbersToShow = 5,
  getRowClassName = null,
  fullHeight = true,
}) => {
  const containerRef = useRef(null);
  const [searchText, setSearchText] = useState("");

  const [currentPage, setCurrentPage] = useState(() => {
    if (tableId) {
      const savedPage = sessionStorage.getItem(`tablePage_${tableId}`);
      return savedPage ? parseInt(savedPage, 10) : 1;
    }
    return 1;
  });

  useEffect(() => {
    if (tableId) {
      sessionStorage.setItem(`tablePage_${tableId}`, currentPage);
    }
  }, [currentPage, tableId]);

  useEffect(() => {
    if (tableId) {
      const savedPage = sessionStorage.getItem(`tablePage_${tableId}`);
      setCurrentPage(savedPage ? parseInt(savedPage, 10) : 1);
    } else {
      setCurrentPage(1);
    }
  }, [tableId]);

  const fileName = customFileName || tableId || "Inhouse_Task_Report";

  /* ---------------- Global Search Logic ---------------- */
  const filteredRows = useMemo(() => {
    if (!searchText || !searchText.trim()) return rows;
    const query = searchText.trim().toLowerCase();
    return (rows || []).filter((row) =>
      Object.values(row).some((val) => {
        if (val === null || val === undefined) return false;
        if (typeof val === "object") {
          return Object.values(val).some(
            (v) => v !== null && v !== undefined && v.toString().toLowerCase().includes(query)
          );
        }
        return val.toString().toLowerCase().includes(query);
      })
    );
  }, [rows, searchText]);

  /* ---------------- Reset page on data change ---------------- */
  const isFirstMount = useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    setCurrentPage(1);
  }, [searchText, itemsPerPage]);

  useEffect(() => {
    const maxPage = Math.ceil(filteredRows.length / itemsPerPage);
    if (maxPage > 0 && currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [filteredRows.length, itemsPerPage, currentPage]);

  /* ---------------- Pagination Logic ---------------- */
  const totalPages = Math.ceil(filteredRows.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentRows = filteredRows.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const renderPaginationItems = () => {
    let items = [];
    let startPage = Math.max(1, currentPage - Math.floor(maxPageNumbersToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPageNumbersToShow - 1);

    if (endPage - startPage + 1 < maxPageNumbersToShow) {
      startPage = Math.max(1, endPage - maxPageNumbersToShow + 1);
    }

    if (startPage > 1) {
      items.push(
        <button
          key={1}
          type="button"
          onClick={() => handlePageChange(1)}
          className={`px-2.5 py-1 text-xs font-semibold rounded-md border transition-all ${1 === currentPage
            ? "bg-[#133A69] text-white border-[#133A69]"
            : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
            }`}
        >
          1
        </button>
      );
      if (startPage > 2) {
        items.push(
          <span key="start-ellipsis" className="px-1 text-xs text-slate-400 select-none">
            ...
          </span>
        );
      }
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <button
          key={number}
          type="button"
          onClick={() => handlePageChange(number)}
          className={`px-2.5 py-1 text-xs font-semibold rounded-md border transition-all ${number === currentPage
            ? "bg-[#133A69] text-white border-[#133A69] shadow-xs"
            : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
            }`}
        >
          {number}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(
          <span key="end-ellipsis" className="px-1 text-xs text-slate-400 select-none">
            ...
          </span>
        );
      }
      items.push(
        <button
          key={totalPages}
          type="button"
          onClick={() => handlePageChange(totalPages)}
          className={`px-2.5 py-1 text-xs font-semibold rounded-md border transition-all ${totalPages === currentPage
            ? "bg-[#133A69] text-white border-[#133A69]"
            : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
            }`}
        >
          {totalPages}
        </button>
      );
    }

    return items;
  };

  /* ---------------- Safe Excel Text Extraction ---------------- */
  const extractTextFromReactNode = (node) => {
    if (node === null || node === undefined || typeof node === "boolean") return "";
    if (typeof node === "string" || typeof node === "number") return String(node);
    if (Array.isArray(node)) {
      return node.map(extractTextFromReactNode).filter(Boolean).join(" ").trim();
    }
    if (typeof node === "object" && node.props) {
      if (node.props.children) {
        return extractTextFromReactNode(node.props.children);
      }
    }
    return "";
  };

  const getExportCellValue = (col, row) => {
    if (typeof col.exportValue === "function") {
      return col.exportValue(row);
    }
    if (typeof col.valueGetter === "function") {
      return col.valueGetter({ row, col });
    }

    const rawVal = row[col.field];

    if (rawVal !== undefined && rawVal !== null) {
      if (typeof rawVal === "string" || typeof rawVal === "number" || typeof rawVal === "boolean") {
        return rawVal;
      }
      if (typeof rawVal === "object") {
        const extracted =
          rawVal.name || rawVal.Name || rawVal.WhaClientName || rawVal.project_name || rawVal.module_name || rawVal.Work;
        if (extracted) return extracted;
      }
    }

    if (typeof col.renderCell === "function") {
      try {
        const rendered = col.renderCell({
          row,
          value: rawVal,
          field: col.field,
        });
        const extractedText = extractTextFromReactNode(rendered);
        if (extractedText) return extractedText;
      } catch (err) {
        // ignore
      }
    }

    if (typeof rawVal === "object" && rawVal !== null) {
      return "--";
    }

    return rawVal ?? "--";
  };

  const handleDownloadExcel = () => {
    const rowsToExport = filteredRows;

    if (!rowsToExport || rowsToExport.length === 0) {
      alert("No data available to export!");
      return;
    }

    const isActionCol = (col) => {
      const field = String(col.field || "").toLowerCase();
      if (
        field === "action" ||
        field === "actions" ||
        field === "showtask" ||
        field === "accepttask" ||
        field === "holdtask" ||
        field === "assignrequest" ||
        field === "completetask" ||
        field === "editcallno" ||
        field === "checkingtask"
      ) {
        return true;
      }
      const header = String(col.headerName || "").toLowerCase();
      if (header.includes("action") || header.includes("show") || header.includes("task")) {
        if (header.includes("task work") || header.includes("time") || header.includes("status")) {
          return false;
        }
        return true;
      }
      return false;
    };

    const cols = (columns || []).filter((col) => !isActionCol(col));

    if (cols.length === 0) {
      alert("No exportable columns found!");
      return;
    }

    const dataForExcel = rowsToExport.map((row) => {
      const newRow = {};
      cols.forEach((col) => {
        const header = typeof col.headerName === "string" ? col.headerName : col.field || "Column";
        newRow[header] = getExportCellValue(col, row);
      });
      return newRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(dataForExcel, { origin: "A3" });

    worksheet["A1"] = {
      v: `Report: ${fileName}`,
      t: "s",
      s: { font: { bold: true } },
    };

    worksheet["A2"] = {
      v: `Date: ${new Date().toLocaleDateString("en-IN")}`,
      t: "s",
      s: { font: { bold: true } },
    };

    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    range.s.r = 0;
    worksheet["!ref"] = XLSX.utils.encode_range(range);

    worksheet["!cols"] = cols.map((col) => {
      const header = typeof col.headerName === "string" ? col.headerName : col.field || "Column";
      const maxLen = Math.max(
        header.length,
        ...dataForExcel.slice(0, 100).map((r) => String(r[header] || "").length)
      );
      return { wch: Math.min(Math.max(maxLen + 3, 12), 50) };
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  return (
    <div
      ref={containerRef}
      className={`w-full min-w-0 ${fullHeight ? "flex flex-col flex-grow min-h-0 h-full overflow-hidden" : ""
        }`}
    >
      {/* 🔍 Search + Export Bar */}
      <div className="flex flex-wrap items-center justify-between gap-2.5 mb-2.5 flex-shrink-0">
        <div className="relative w-full sm:w-80">
          <input
            type="text"
            className="w-full pl-9 pr-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg focus:border-[#133A69] focus:ring-2 focus:ring-blue-500/20 outline-none transition-all placeholder:text-slate-400 font-medium shadow-xs"
            placeholder="Global Search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
        </div>

        <button
          type="button"
          onClick={handleDownloadExcel}
          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 rounded-lg shadow-xs hover:shadow transition-all"
        >
          <Download size={14} />
          <span>Export to Excel</span>
        </button>
      </div>

      {/* Table Container */}
      <div
        className="w-full border border-slate-200 rounded-xl overflow-hidden shadow-xs bg-white flex flex-col flex-grow min-h-0"
      >
        <div
          className={`w-full overflow-x-auto overflow-y-auto ${fullHeight ? "flex-grow min-h-0" : "max-h-[500px]"
            }`}
          style={{ height: fullHeight ? "100%" : "auto" }}
        >
          <table className="w-full border-collapse text-left text-xs">
            {/* Dark Navy Table Header */}
            <thead className="sticky top-0 z-20 bg-[#133A69] text-white select-none shadow-sm">
              <tr>
                {columns.map((col, index) => {
                  const widthVal = col.width
                    ? typeof col.width === "number"
                      ? `${col.width}px`
                      : col.width
                    : undefined;
                  const minWidthVal = col.minWidth
                    ? typeof col.minWidth === "number"
                      ? `${col.minWidth}px`
                      : col.minWidth
                    : widthVal;
                  const maxWidthVal = col.maxWidth
                    ? typeof col.maxWidth === "number"
                      ? `${col.maxWidth}px`
                      : col.maxWidth
                    : undefined;

                  const isNarrow =
                    col.field === "tokan" ||
                    col.field === "CallNo" ||
                    col.field === "id" ||
                    (col.width && (typeof col.width === "number" ? col.width : parseInt(col.width, 10)) <= 90);

                  return (
                    <th
                      key={col.field || index}
                      className={`${isNarrow ? "px-2" : "px-3"} py-2.5 font-bold uppercase tracking-wider whitespace-nowrap text-white text-[11px] border-b border-[#0d284a]`}
                      style={{
                        width: widthVal,
                        minWidth: minWidthVal,
                        maxWidth: maxWidthVal,
                        textAlign: col.align || "left",
                      }}
                    >
                      {col.headerName}
                    </th>
                  );
                })}
              </tr>
            </thead>

            {/* Table Body */}
            <tbody className="divide-y divide-slate-100 bg-white">
              {currentRows.length > 0 ? (
                currentRows.map((row, rowIndex) => {
                  const customRowClass = getRowClassName ? getRowClassName(row) : "";
                  const isEven = rowIndex % 2 === 0;

                  return (
                    <tr
                      key={row.id || row.MainId || rowIndex}
                      onClick={(e) => {
                        // Prevent triggering row navigation when clicking on buttons, inputs, links, selects, dropdowns, or actions
                        if (
                          e.target.closest("button") ||
                          e.target.closest("a") ||
                          e.target.closest("input") ||
                          e.target.closest("select") ||
                          e.target.closest("textarea") ||
                          e.target.closest(".action-btn") ||
                          e.target.closest("[data-no-row-click]")
                        ) {
                          return;
                        }
                        if (onRowClick) {
                          onRowClick(row, e);
                        }
                      }}
                      className={`transition-colors duration-150 ${onRowClick ? "cursor-pointer hover:bg-blue-50/70" : "hover:bg-slate-100/90"
                        } ${customRowClass} ${isEven ? "bg-white" : "bg-slate-50/60"}`}
                    >
                      {columns.map((col, colIndex) => {
                        const cellValue = row[col.field];
                        const widthVal = col.width
                          ? typeof col.width === "number"
                            ? `${col.width}px`
                            : col.width
                          : undefined;
                        const minWidthVal = col.minWidth
                          ? typeof col.minWidth === "number"
                            ? `${col.minWidth}px`
                            : col.minWidth
                          : widthVal;
                        const maxWidthVal = col.maxWidth
                          ? typeof col.maxWidth === "number"
                            ? `${col.maxWidth}px`
                            : col.maxWidth
                          : undefined;

                        const isNarrow =
                          col.field === "tokan" ||
                          col.field === "CallNo" ||
                          col.field === "id" ||
                          (col.width && (typeof col.width === "number" ? col.width : parseInt(col.width, 10)) <= 90);

                        return (
                          <td
                            key={col.field || colIndex}
                            className={`${isNarrow ? "px-2" : "px-3"} py-2 whitespace-nowrap align-middle text-slate-700 text-xs`}
                            style={{
                              width: widthVal,
                              minWidth: minWidthVal,
                              maxWidth: maxWidthVal,
                              textAlign: col.align || "left",
                            }}
                          >
                            {col.renderCell
                              ? col.renderCell({
                                row,
                                value: cellValue,
                                field: col.field,
                              })
                              : typeof cellValue === "object" && cellValue !== null
                                ? cellValue?.name ||
                                cellValue?.Name ||
                                cellValue?.WhaClientName ||
                                cellValue?.Work ||
                                "--"
                                : cellValue ?? "--"}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-12 text-center text-slate-400">
                    {emptyComponent || (
                      <div className="flex flex-col items-center justify-center gap-1">
                        <span className="text-sm font-semibold text-slate-600">No tasks found</span>
                        <span className="text-xs text-slate-400">
                          There are currently no tasks in this section.
                        </span>
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer Summary & Pagination */}
        <div className="px-4 py-2 bg-slate-50/90 border-t border-slate-200 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500 flex-shrink-0">
          <div>
            Showing{" "}
            <span className="font-semibold text-slate-700">
              {filteredRows.length > 0 ? indexOfFirstItem + 1 : 0}
            </span>{" "}
            to{" "}
            <span className="font-semibold text-slate-700">
              {Math.min(indexOfLastItem, filteredRows.length)}
            </span>{" "}
            of <span className="font-semibold text-slate-700">{filteredRows.length}</span> entries
          </div>

          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="p-1 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                title="First Page"
              >
                <ChevronsLeft size={14} />
              </button>

              <button
                type="button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-1 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                title="Previous Page"
              >
                <ChevronLeft size={14} />
              </button>

              <div className="flex items-center gap-1 mx-1">{renderPaginationItems()}</div>

              <button
                type="button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-1 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                title="Next Page"
              >
                <ChevronRight size={14} />
              </button>

              <button
                type="button"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="p-1 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                title="Last Page"
              >
                <ChevronsRight size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportStyleTable;
